import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './StakeholderNetworkModule.module.css';
import { Project } from '../../types';
import { stakeholderNetworkAPI } from '../../services/api';

interface StakeholderNetworkModuleProps {
  project: Project;
}

interface NetworkNode {
  id: string;
  label: string;
  type: string;
  influence?: string;
  interest?: string;
  sentiment?: string;
  engagementLevel?: string;
  organization?: string;
  role?: string;
  size: number;
  color: string;
  x: number;
  y: number;
}

interface NetworkEdge {
  id: string;
  source: string;
  target: string;
  weight: number;
  label?: string;
  color: string;
  dashed?: boolean;
}

interface PowerInterestEntry {
  id: string;
  name: string;
  type: string;
  power: number;
  interest: number;
}

interface Coalition {
  type: string;
  members: { id: string; name: string }[];
  size: number;
  dominantSentiment: string;
  cohesion: string;
}

const TYPE_LABELS: Record<string, string> = {
  government: 'Overheid', ngo: 'NGO', business: 'Bedrijfsleven', citizen: 'Burger',
  media: 'Media', education: 'Onderwijs', healthcare: 'Zorg', infrastructure: 'Infra',
  community: 'Gemeenschap', project: 'Project', overig: 'Overig'
};

const TYPE_COLORS: Record<string, string> = {
  government: '#3b82f6', ngo: '#10b981', business: '#f59e0b', citizen: '#8b5cf6',
  media: '#ef4444', education: '#06b6d4', healthcare: '#ec4899', infrastructure: '#f97316',
  community: '#14b8a6', project: '#E6007E', overig: '#94a3b8'
};

export const StakeholderNetworkModule: React.FC<StakeholderNetworkModuleProps> = ({ project }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState(true);
  const [nodes, setNodes] = useState<NetworkNode[]>([]);
  const [edges, setEdges] = useState<NetworkEdge[]>([]);
  const [clusters, setClusters] = useState<Record<string, any>>({});
  const [powerInterest, setPowerInterest] = useState<any>(null);
  const [sentimentSummary, setSentimentSummary] = useState<any>(null);
  const [coalitions, setCoalitions] = useState<Coalition[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<NetworkNode | null>(null);
  const [view, setView] = useState<'network' | 'power_interest' | 'coalitions'>('network');
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);

  const fetchNetwork = useCallback(async () => {
    try {
      setLoading(true);
      const data = await stakeholderNetworkAPI.getNetwork(project.id);
      setNodes(data.network?.nodes || []);
      setEdges(data.network?.edges || []);
      setClusters(data.clusters || {});
      setPowerInterest(data.powerInterestGrid || null);
      setSentimentSummary(data.sentimentSummary || null);
      setCoalitions(data.coalitions || []);
      setStats(data.stats || null);
    } catch (error) {
      console.error('Failed to fetch network:', error);
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { fetchNetwork(); }, [fetchNetwork]);

  // Canvas-based network rendering
  useEffect(() => {
    if (view !== 'network' || !canvasRef.current || nodes.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.parentElement?.getBoundingClientRect();
    canvas.width = rect?.width || 800;
    canvas.height = 600;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const scale = Math.min(canvas.width, canvas.height) / 500;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw edges
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return;

      const sx = centerX + sourceNode.x * scale;
      const sy = centerY + sourceNode.y * scale;
      const tx = centerX + targetNode.x * scale;
      const ty = centerY + targetNode.y * scale;

      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(tx, ty);
      ctx.strokeStyle = edge.color || '#e2e8f0';
      ctx.lineWidth = edge.weight * 0.5;
      if (edge.dashed) ctx.setLineDash([5, 5]);
      else ctx.setLineDash([]);
      ctx.stroke();
    });

    // Draw nodes
    nodes.forEach(node => {
      const x = centerX + node.x * scale;
      const y = centerY + node.y * scale;
      const radius = node.size * scale * 0.15;
      const isHovered = hoveredNode === node.id;
      const isSelected = selectedNode?.id === node.id;

      // Node circle
      ctx.beginPath();
      ctx.arc(x, y, radius + (isHovered || isSelected ? 3 : 0), 0, Math.PI * 2);
      ctx.fillStyle = node.color;
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = '#E6007E';
        ctx.lineWidth = 3;
        ctx.stroke();
      }

      // Label
      ctx.fillStyle = '#1e293b';
      ctx.font = `${isHovered || isSelected ? 'bold ' : ''}${11 * scale}px 'Open Sans', sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(node.label, x, y + radius + 14 * scale);
    });
  }, [nodes, edges, view, hoveredNode, selectedNode]);

  // Canvas click handler
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const scale = Math.min(canvas.width, canvas.height) / 500;

    const clicked = nodes.find(node => {
      const nx = centerX + node.x * scale;
      const ny = centerY + node.y * scale;
      const dist = Math.sqrt((x - nx) ** 2 + (y - ny) ** 2);
      return dist < node.size * scale * 0.2;
    });

    setSelectedNode(clicked || null);
  };

  if (loading) {
    return <div className={styles.container}><div className={styles.loading}>Laden...</div></div>;
  }

  if (nodes.length <= 1) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Stakeholder Netwerk</h1>
          <p className={styles.subtitle}>Visuele netwerkanalyse van stakeholder relaties</p>
        </div>
        <div className={styles.emptyState}>
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><circle cx="4" cy="8" r="2"/><circle cx="20" cy="8" r="2"/><circle cx="4" cy="16" r="2"/><circle cx="20" cy="16" r="2"/><line x1="6" y1="8" x2="9" y2="10"/><line x1="18" y1="8" x2="15" y2="10"/><line x1="6" y1="16" x2="9" y2="14"/><line x1="18" y1="16" x2="15" y2="14"/></svg>
          <h3>Geen stakeholders gevonden</h3>
          <p>Voeg eerst stakeholders toe via Relatiebeheer om het netwerk te visualiseren.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Stakeholder Netwerk</h1>
          <p className={styles.subtitle}>{stats?.totalStakeholders} stakeholders, {stats?.totalConnections} verbindingen</p>
        </div>
        <div className={styles.viewToggle}>
          <button className={`${styles.toggleButton} ${view === 'network' ? styles.toggleActive : ''}`} onClick={() => setView('network')}>Netwerk</button>
          <button className={`${styles.toggleButton} ${view === 'power_interest' ? styles.toggleActive : ''}`} onClick={() => setView('power_interest')}>Invloed-Belang</button>
          <button className={`${styles.toggleButton} ${view === 'coalitions' ? styles.toggleActive : ''}`} onClick={() => setView('coalitions')}>Coalities</button>
        </div>
      </div>

      {/* Sentiment summary */}
      {sentimentSummary && (
        <div className={styles.sentimentBar}>
          <div className={styles.sentimentSegment} style={{ width: `${(sentimentSummary.positive / (nodes.length - 1) * 100) || 0}%`, background: '#22c55e' }} title={`Positief: ${sentimentSummary.positive}`} />
          <div className={styles.sentimentSegment} style={{ width: `${(sentimentSummary.neutral / (nodes.length - 1) * 100) || 0}%`, background: '#94a3b8' }} title={`Neutraal: ${sentimentSummary.neutral}`} />
          <div className={styles.sentimentSegment} style={{ width: `${(sentimentSummary.negative / (nodes.length - 1) * 100) || 0}%`, background: '#ef4444' }} title={`Negatief: ${sentimentSummary.negative}`} />
        </div>
      )}

      {view === 'network' && (
        <div className={styles.networkView}>
          <div className={styles.canvasWrapper}>
            <canvas ref={canvasRef} onClick={handleCanvasClick} className={styles.networkCanvas} />
          </div>

          {/* Legend */}
          <div className={styles.legend}>
            {Object.entries(clusters).map(([type, data]) => (
              <span key={type} className={styles.legendItem}>
                <span className={styles.legendDot} style={{ background: TYPE_COLORS[type] || '#94a3b8' }} />
                {TYPE_LABELS[type] || type} ({data.count})
              </span>
            ))}
          </div>

          {/* Selected node detail */}
          {selectedNode && selectedNode.id !== 'project' && (
            <div className={styles.nodeDetail}>
              <h3>{selectedNode.label}</h3>
              <div className={styles.detailGrid}>
                <div><span className={styles.detailLabel}>Type</span><span>{TYPE_LABELS[selectedNode.type] || selectedNode.type}</span></div>
                <div><span className={styles.detailLabel}>Invloed</span><span>{selectedNode.influence || '-'}</span></div>
                <div><span className={styles.detailLabel}>Belang</span><span>{selectedNode.interest || '-'}</span></div>
                <div><span className={styles.detailLabel}>Sentiment</span><span>{selectedNode.sentiment || '-'}</span></div>
                <div><span className={styles.detailLabel}>Engagement</span><span>{selectedNode.engagementLevel || '-'}</span></div>
                {selectedNode.organization && <div><span className={styles.detailLabel}>Organisatie</span><span>{selectedNode.organization}</span></div>}
              </div>
            </div>
          )}
        </div>
      )}

      {view === 'power_interest' && powerInterest && (
        <div className={styles.powerInterestView}>
          <div className={styles.grid}>
            <div className={styles.gridQuadrant}>
              <h4 className={styles.quadrantTitle}>Key Players</h4>
              <p className={styles.quadrantDesc}>Hoge invloed, hoog belang - Actief managen</p>
              {powerInterest.highPowerHighInterest.map((entry: PowerInterestEntry) => (
                <div key={entry.id} className={styles.gridEntry}>
                  <span className={styles.gridDot} style={{ background: TYPE_COLORS[entry.type] || '#94a3b8' }} />
                  <span>{entry.name}</span>
                  <span className={styles.gridType}>{TYPE_LABELS[entry.type]}</span>
                </div>
              ))}
              {powerInterest.highPowerHighInterest.length === 0 && <p className={styles.noEntries}>Geen stakeholders</p>}
            </div>

            <div className={styles.gridQuadrant}>
              <h4 className={styles.quadrantTitle}>Tevreden houden</h4>
              <p className={styles.quadrantDesc}>Hoge invloed, laag belang - Tevreden houden</p>
              {powerInterest.highPowerLowInterest.map((entry: PowerInterestEntry) => (
                <div key={entry.id} className={styles.gridEntry}>
                  <span className={styles.gridDot} style={{ background: TYPE_COLORS[entry.type] || '#94a3b8' }} />
                  <span>{entry.name}</span>
                  <span className={styles.gridType}>{TYPE_LABELS[entry.type]}</span>
                </div>
              ))}
              {powerInterest.highPowerLowInterest.length === 0 && <p className={styles.noEntries}>Geen stakeholders</p>}
            </div>

            <div className={styles.gridQuadrant}>
              <h4 className={styles.quadrantTitle}>Informeren</h4>
              <p className={styles.quadrantDesc}>Lage invloed, hoog belang - Op de hoogte houden</p>
              {powerInterest.lowPowerHighInterest.map((entry: PowerInterestEntry) => (
                <div key={entry.id} className={styles.gridEntry}>
                  <span className={styles.gridDot} style={{ background: TYPE_COLORS[entry.type] || '#94a3b8' }} />
                  <span>{entry.name}</span>
                  <span className={styles.gridType}>{TYPE_LABELS[entry.type]}</span>
                </div>
              ))}
              {powerInterest.lowPowerHighInterest.length === 0 && <p className={styles.noEntries}>Geen stakeholders</p>}
            </div>

            <div className={styles.gridQuadrant}>
              <h4 className={styles.quadrantTitle}>Monitoren</h4>
              <p className={styles.quadrantDesc}>Lage invloed, laag belang - Observeren</p>
              {powerInterest.lowPowerLowInterest.map((entry: PowerInterestEntry) => (
                <div key={entry.id} className={styles.gridEntry}>
                  <span className={styles.gridDot} style={{ background: TYPE_COLORS[entry.type] || '#94a3b8' }} />
                  <span>{entry.name}</span>
                  <span className={styles.gridType}>{TYPE_LABELS[entry.type]}</span>
                </div>
              ))}
              {powerInterest.lowPowerLowInterest.length === 0 && <p className={styles.noEntries}>Geen stakeholders</p>}
            </div>
          </div>
        </div>
      )}

      {view === 'coalitions' && (
        <div className={styles.coalitionsView}>
          {coalitions.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>Geen coalities gedetecteerd</h3>
              <p>Coalities worden gevormd door groepen van 2+ stakeholders van hetzelfde type.</p>
            </div>
          ) : (
            <div className={styles.coalitionsList}>
              {coalitions.map((coalition, idx) => (
                <div key={idx} className={styles.coalitionCard}>
                  <div className={styles.coalitionHeader}>
                    <span className={styles.coalitionDot} style={{ background: TYPE_COLORS[coalition.type] || '#94a3b8' }} />
                    <h4>{TYPE_LABELS[coalition.type] || coalition.type}</h4>
                    <span className={styles.coalitionSize}>{coalition.size} leden</span>
                  </div>
                  <div className={styles.coalitionMeta}>
                    <span>Sentiment: <strong>{coalition.dominantSentiment}</strong></span>
                    <span>Cohesie: <strong>{coalition.cohesion === 'high' ? 'Hoog' : 'Gemengd'}</strong></span>
                  </div>
                  <div className={styles.coalitionMembers}>
                    {coalition.members.map(m => (
                      <span key={m.id} className={styles.memberTag}>{m.name}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
